/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, {
	memo,
	useCallback,
	useMemo,
	useEffect,
	useContext,
	type ComponentPropsWithoutRef,
	useRef,
} from 'react';
import { EntryPointContainer, type PreloadedEntryPoint } from 'react-relay';
import { JiraInvitePeopleModalContextProvider } from '@jira/project-settings__invite-people-modal-context/src/JiraInvitePeopleModalContextProvider.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import UFOLabel from '@jira/platform__app-framework-ufo-label/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import GlobalShortcutsPlatform from '@atlassian/global-shortcuts/ui';
import type {
	GlobalShortcutsItem,
	GlobalShortcutsProps as PlatformGlobalShortcutsProps,
} from '@atlassian/global-shortcuts/types';
import {
	type MenuItemConfigData,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { ContextualAnalyticsData } from '@jira/navigation-apps__sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { useTeamsAppShortcut } from './apps/teams/index.tsx';
import { useLicenseFilterMap } from './use-license-filter-map/index.tsx';
import { ReorderShortcutsContainer } from './reorder-shortcuts-container.tsx';
import { useResolveInvitedAppShortcuts } from './useResolveInvitedAppShortcuts.tsx';
import type { GlobalShortcutsInvitedAppShortcutsRecommendationsComponent } from './invited-app-shortcuts-recommendations/GlobalShortcutsInvitedAppShortcutsRecommendations.tsx';

type GlobalShortcutsProps = {
	appItemsData?: MenuItemConfigData[];
	handleUpdateGetFilteredProductLinks?: PlatformGlobalShortcutsProps['onUpdateGetFilteredProductLinks'];
	// Present only when the productionisation cohort gates are on; absence
	// falls through to the existing experiment / control branch.
	invitedAppShortcutsRecommendationsEntryPoint?: PreloadedEntryPoint<GlobalShortcutsInvitedAppShortcutsRecommendationsComponent>;
};

type GlobalShortcutsWithTeamsProps = GlobalShortcutsProps & {
	teamsAppConfig?: GlobalShortcutsItem;
};

type GetFilteredProductLinks = Parameters<
	NonNullable<
		ComponentPropsWithoutRef<typeof GlobalShortcutsPlatform>['onUpdateGetFilteredProductLinks']
	>
>[0];

const GlobalShortcutsComponent = memo(
	({
		appItemsData,
		teamsAppConfig,
		handleUpdateGetFilteredProductLinks,
		invitedAppShortcutsRecommendationsEntryPoint,
	}: GlobalShortcutsWithTeamsProps) => {
		const cloudId = useCloudId();
		const userId = useAccountId();
		const { visibleMenuIds } = useContext(NavItemsContext);
		const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();
		const getFilteredProductLinksRef = useRef<GetFilteredProductLinks | null>(null);

		const isEligibleToSeeInvitedAppShortcuts =
			// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('twcg-460-personalised-invite-notifications-m2-gate') &&
			invitedAppShortcutsExperiment.ineligibilityReason === null &&
			invitedAppShortcutsExperiment.invitedApps?.confluence?.eligible === true;

		const isInvitedAppShortcutsEnabled =
			isEligibleToSeeInvitedAppShortcuts &&
			expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false);

		const licenceFilterMap = useLicenseFilterMap(teamsAppConfig);

		const hasConfluenceLicence: boolean = Boolean(
			licenceFilterMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
		);

		const shouldShowInvitedAppShortcuts: boolean = useMemo(
			() => isInvitedAppShortcutsEnabled && hasConfluenceLicence,
			[isInvitedAppShortcutsEnabled, hasConfluenceLicence],
		);

		// sync updated shortcut items across the other atomic components
		const onUpdateGetFilteredProductLinks: ComponentPropsWithoutRef<
			typeof GlobalShortcutsPlatform
		>['onUpdateGetFilteredProductLinks'] = useCallback(
			(getFilteredProductLinks: GetFilteredProductLinks) => {
				if (handleUpdateGetFilteredProductLinks) {
					handleUpdateGetFilteredProductLinks(getFilteredProductLinks);
				}

				// this should be tidied up after the productionisation A/A test is complete
				if (fg('twcg-460-personalised-invite-notifications-m2-gate')) {
					getFilteredProductLinksRef.current = getFilteredProductLinks;
					invitedAppShortcutsExperiment.updateFromAppAccessFilter({
						getFilteredProductLinks,
						limitToOnlyApsKeys: ['CONFLUENCE'],
						visibleMenuIds,
					});
				}
			},
			[handleUpdateGetFilteredProductLinks, invitedAppShortcutsExperiment, visibleMenuIds],
		);

		// Productionisation dispatch. The cohort is resolved in
		// `nav4SidebarContentCoreEntryPoint`, so a truthy entrypoint already means
		// the user is in the test cohort (kill-switch gate on + experiment
		// `isEnabled`); no experiment re-check is needed here. The exposure fires
		// in `GlobalShortcutsWithInvitedAppRecommendations.tsx` once recs render.
		// Missing inputs fall through to the legacy / control branch.
		const willDispatchProductionisation = Boolean(
			invitedAppShortcutsRecommendationsEntryPoint && userId,
		);

		useEffect(() => {
			if (isEligibleToSeeInvitedAppShortcuts) {
				if (willDispatchProductionisation) {
					invitedAppShortcutsExperiment.fireFeatureExposed({
						// technically, we will only fire the exposures for twcg-460-preload-grs-invited-app-shortcuts's control cohort here
						isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
						overrideMode: 'productionisation',
					});
				} else {
					invitedAppShortcutsExperiment.fireFeatureExposed({
						isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
					});
				}
			}
		}, [
			isEligibleToSeeInvitedAppShortcuts,
			invitedAppShortcutsExperiment,
			willDispatchProductionisation,
		]);

		useEffect(() => {
			if (getFilteredProductLinksRef.current) {
				invitedAppShortcutsExperiment.updateFromAppAccessFilter({
					getFilteredProductLinks: getFilteredProductLinksRef.current,
					limitToOnlyApsKeys: ['CONFLUENCE'],
					visibleMenuIds,
				});
			}
		}, [invitedAppShortcutsExperiment, visibleMenuIds]);

		// Shared fallback for the error boundary AND the absent-entrypoint
		// branch; `useCallback` per AFM `JSErrorBoundary` convention.
		const renderLegacyOrControlSidebar = useCallback((): React.JSX.Element => {
			if (shouldShowInvitedAppShortcuts) {
				return (
					<InvitedAppShortcutsContainer
						shortcutsConfig={licenceFilterMap}
						resolverResult={invitedAppShortcutsExperiment}
					>
						{({ referrableShortcuts }) => (
							<ReorderShortcutsContainer
								shortcutsConfig={referrableShortcuts}
								appItemsData={appItemsData}
							>
								{({ shortcuts }) => (
									<GlobalShortcutsPlatform
										cloudId={cloudId}
										shortcutItems={shortcuts}
										product="jira"
										onUpdateGetFilteredProductLinks={onUpdateGetFilteredProductLinks}
									/>
								)}
							</ReorderShortcutsContainer>
						)}
					</InvitedAppShortcutsContainer>
				);
			}

			return (
				<ReorderShortcutsContainer shortcutsConfig={licenceFilterMap} appItemsData={appItemsData}>
					{({ shortcuts }) => (
						<GlobalShortcutsPlatform
							cloudId={cloudId}
							shortcutItems={shortcuts}
							product="jira"
							onUpdateGetFilteredProductLinks={onUpdateGetFilteredProductLinks}
						/>
					)}
				</ReorderShortcutsContainer>
			);
		}, [
			appItemsData,
			cloudId,
			invitedAppShortcutsExperiment,
			licenceFilterMap,
			onUpdateGetFilteredProductLinks,
			shouldShowInvitedAppShortcuts,
		]);

		const invitedAppRecommendationsProps = useMemo(
			() => ({
				appItemsData,
				cloudId,
				onUpdateGetFilteredProductLinks,
				shortcutsConfig: licenceFilterMap,
				// Type-only coalesce; `willDispatchProductionisation` ensures
				// `userId` is truthy at render time so '' is unreachable.
				userId: userId ?? '',
			}),
			[appItemsData, cloudId, licenceFilterMap, onUpdateGetFilteredProductLinks, userId],
		);

		if (willDispatchProductionisation && invitedAppShortcutsRecommendationsEntryPoint) {
			return (
				<ContextualAnalyticsData menuId="gsc-invited-app-shortcuts-productionisation">
					<JSErrorBoundary
						id="gsc-invited-app-shortcuts-productionisation"
						packageName="jiraNavigationAppsSidebarNav4SidebarsContentCore"
						teamName="growth-puma"
						fallback={renderLegacyOrControlSidebar}
					>
						<EntryPointContainer
							entryPointReference={invitedAppShortcutsRecommendationsEntryPoint}
							props={invitedAppRecommendationsProps}
						/>
					</JSErrorBoundary>
				</ContextualAnalyticsData>
			);
		}

		return renderLegacyOrControlSidebar();
	},
);

// Wrapper required to use teams app shortcut hook
const GlobalShortcutsInvitePeopleWrapper = (props: GlobalShortcutsProps) => {
	const { component: teamsAppComponent, config: teamsAppConfig } = useTeamsAppShortcut();
	return (
		<>
			{teamsAppComponent}
			<GlobalShortcutsComponent {...props} teamsAppConfig={teamsAppConfig} />
		</>
	);
};

const GlobalShortcutsInvitePeopleWrapperWithErrorBoundary = (props: GlobalShortcutsProps) => {
	return (
		<JSErrorBoundary
			id="globalShortcutsInvitePeopleWrapper"
			teamName="People and Teams Collective"
			fallback="unmount"
			packageName="jiraNavigationAppsSidebarNav4SidebarsContentCore"
		>
			<GlobalShortcutsInvitePeopleWrapper {...props} />
		</JSErrorBoundary>
	);
};

// The teams app will sit under its own feature gate / experiement
export const GlobalShortcutsWithInvitePeopleProvider = (
	props: GlobalShortcutsProps,
): React.JSX.Element => {
	return (
		<UFOLabel name="nav4-sidebar-global-shortcuts">
			<JiraInvitePeopleModalContextProvider
				renderedInProjectContext={false}
				shouldDisplaySuccessModal={false}
			>
				{fg('teams-shortcut-error-boundary-jira') ? (
					<GlobalShortcutsInvitePeopleWrapperWithErrorBoundary {...props} />
				) : (
					<GlobalShortcutsInvitePeopleWrapper {...props} />
				)}
			</JiraInvitePeopleModalContextProvider>
		</UFOLabel>
	);
};

// Teams is not included by default, so we export the component without it
export const GlobalShortcuts = (props: GlobalShortcutsProps): React.JSX.Element => {
	return (
		<UFOLabel name="nav4-sidebar-global-shortcuts">
			<JSErrorBoundary
				id="globalShortcuts"
				teamName="Navigation"
				fallback="unmount"
				packageName="jiraNavigationAppsSidebarNav4SidebarsContentCore"
			>
				<GlobalShortcutsComponent {...props} />
			</JSErrorBoundary>
		</UFOLabel>
	);
};
